import sessionReader from '../store/readHelpers/SessionReader';
import readinessCheckService from './ReadinessCheckService';
import standaloneTestStarter from './StandaloneTestStarter';
import apiService from './api/ApiService';
import navigationService from './NavigationService';
import alertService from './AlertService';
import timeService from './TimeService';
import { t } from './i18nService';
import candidateProgressConstants from '../constants/CandidateProgressConstants';
import parentTestService from './ParentTestService';

/**
 * Service for starting non-parent nor child tests
 */
const nonParentNorChildTestStarter = {
    isStarting: false,

    /**
     * Starts a non-parent nor child test and returns a promise if applicable.
     * Note: Errors occurring during standalone test startup (e.g. when hardware access check is enabled)
     * are caught and presented to the user via alertService, resolving normally without propagating rejections.
     *
     * @param {Object} test - The test object containing assessmentRunId and orderNumber
     * @returns {Promise<void>} - A promise that resolves when the start operation finishes
     */
    async startNonParentNorChildTest(test) {
        if (this.isStarting) {
            console.warn('Attempting to start a test in progress', test && test.assessmentRunId);
            return;
        }

        const { assessmentRunId, orderNumber } = test;
        this.isStarting = true;

        try {
            if (sessionReader.isHardwareAccessCheckEnabled()) {
                if (sessionReader.hasReadinessCheck()) {
                    readinessCheckService.restoreStandaloneQuestionSet();
                }

                try {
                    await standaloneTestStarter.startStandaloneTest(test);
                } catch (e) {
                    console.error('Error when starting standalone Test', e);
                    alertService.showError(t('alert.livestream.startCountdownFailed'));
                }

                // To avoid waiting for the next scheduled heartbeat in order to fetch a valid remaining-time.
                await timeService.fetchTimeRemaining(true, true);
                return;
            }

            if (sessionReader.hasReadinessCheck()) {
                readinessCheckService.restoreStandaloneQuestionSet();
                apiService.notifyClientEvent(candidateProgressConstants.IN_PROGRESS, '', true);
                navigationService.navigateToFirstQuestion();
                return;
            }

            await parentTestService.startChildTestAndNavigate(assessmentRunId, orderNumber >= 1);
        } finally {
            this.isStarting = false;
        }
    },
};

export default nonParentNorChildTestStarter;
